import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  PlayIcon, 
  DocumentTextIcon, 
  ClockIcon,
  CodeBracketIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { listApiKeys } from '../../services/apiKeyService';
import ApiDocumentation from '../../components/api-playground/SwaggerUIEmbed';
import ApiTemplates from '../../components/api-playground/ApiTemplates';
import RequestBuilder from '../../components/api-playground/RequestBuilder';
import ResponseViewer from '../../components/api-playground/ResponseViewer';
import CodeGenerator from '../../components/api-playground/CodeGenerator';
import RequestHistory from '../../components/api-playground/RequestHistory';
import WelcomeToPlaygroundCard from '../../components/api-playground/WelcomeToPlaygroundCard';
import { 
  ApiRequest, 
  ApiResponse, 
  executeApiRequest, 
  saveRequestToHistory,
  RequestTemplate 
} from '../../services/apiPlaygroundService';

type TabType = 'builder' | 'swagger' | 'templates' | 'history';

const ApiPlaygroundPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('builder');
  const [selectedApiKey, setSelectedApiKey] = useState<string>(''); // This will now be mainly for display/reference
  const [manualApiKeyInput, setManualApiKeyInput] = useState<string>('');
  const [showWelcome, setShowWelcome] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const [currentRequest, setCurrentRequest] = useState<ApiRequest>({
    method: 'POST',
    url: 'https://api.crawlycarl.com/api/v1/crawl-sync',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      url: 'https://example.com',
      target_data: 'Extract the main content and key information',
      deepsearch: false,
      intelligent_search: true,
      debugmode: false
    }, null, 2)
  });
  const [currentResponse, setCurrentResponse] = useState<ApiResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCodeGenerator, setShowCodeGenerator] = useState(false);

  // Fetch API keys for dropdown (informational)
  const { data: apiKeys = [], isLoading: apiKeysLoading } = useQuery({
    queryKey: ['apiKeys'],
    queryFn: listApiKeys,
  });

  // Check if coming from API keys page
  useEffect(() => {
    if (location.state?.showWelcome && location.state?.fromApiKeys) {
      setShowWelcome(true);
      // Clear the state to prevent showing again on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  // Auto-select first API key if available
  useEffect(() => {
    if (apiKeys.length > 0 && !selectedApiKey) {
      setSelectedApiKey(apiKeys[0].id);
    }
  }, [apiKeys, selectedApiKey]);

  const handleExecuteRequest = async () => {
    if (!manualApiKeyInput.trim()) {
      alert('Please paste your API key string in the input field to execute the request.');
      return;
    }

    setIsLoading(true);
    setCurrentResponse(null);

    try {
      // Use manualApiKeyInput for the actual request
      const response = await executeApiRequest(currentRequest, manualApiKeyInput);
      setCurrentResponse(response);
      // Optionally, still save selectedApiKey (ID) if history needs it for reference
      saveRequestToHistory(currentRequest, response, selectedApiKey);
    } catch (error) {
      setCurrentResponse(error as ApiResponse);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateSelect = (template: RequestTemplate) => {
    setCurrentRequest({
      method: template.method,
      url: template.endpoint,
      headers: template.headers,
      body: template.body ? JSON.stringify(template.body, null, 2) : undefined
    });
    setActiveTab('builder');
  };

  const tabs = [
    { id: 'builder' as TabType, name: 'Request Builder', icon: PlayIcon },
    { id: 'swagger' as TabType, name: 'API Documentation', icon: DocumentTextIcon },
    { id: 'templates' as TabType, name: 'Templates', icon: CodeBracketIcon },
    { id: 'history' as TabType, name: 'History', icon: ClockIcon },
  ];

  if (apiKeysLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (apiKeys.length === 0) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-yellow-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No API Keys Found</h3>
        <p className="mt-1 text-sm text-gray-500">
          You need to create an API key before using the playground.
        </p>
        <div className="mt-6">
          <a
            href="/dashboard/api-keys"
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Create API Key
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Card */}
      {showWelcome && (
        <WelcomeToPlaygroundCard
          fromApiKeys={location.state?.fromApiKeys}
          newApiKey={location.state?.newApiKey}
        />
      )}

      {/* Documentation Link Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg rounded-lg">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Complete API Documentation
                </h3>
                <p className="text-blue-100 text-sm">
                  View comprehensive guides, examples, and detailed endpoint documentation
                </p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <a
                href="https://crawlycarl.com/api-docs/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 shadow-sm"
              >
                <DocumentTextIcon className="h-5 w-5 mr-2" />
                View Full Documentation
                <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">API Playground</h1>
              <p className="mt-1 text-sm text-gray-500">
                Test and explore the CrawlyCarl API with interactive tools
              </p>
            </div>
            
            {/* API Key Section */}
            <div className="space-y-4">
              {/* API Key Selector (Informational) */}
              <div>
                <label htmlFor="api-key-select" className="block text-sm font-medium text-gray-700">
                  API Key (Reference)
                </label>
                <select
                  id="api-key-select"
                  value={selectedApiKey}
                  onChange={(e) => setSelectedApiKey(e.target.value)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  {apiKeys.map((key) => (
                    <option key={key.id} value={key.id}>
                      {key.key_name || `Key ${key.id.slice(0, 8)}...`} (${key.id})
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Select a key for quick reference. Paste the actual key below for execution.
                </p>
              </div>

              {/* Manual API Key Input */}
              <div>
                <label htmlFor="manual-api-key-input" className="block text-sm font-medium text-gray-700">
                  API Key String (Paste Here to Use)
                </label>
                <input
                  type="text" // Consider type="password" for better security UX
                  id="manual-api-key-input"
                  value={manualApiKeyInput}
                  onChange={(e) => setManualApiKeyInput(e.target.value)}
                  className="mt-1 block w-full pl-3 pr-3 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  placeholder="sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                />
                 <p className="mt-1 text-xs text-gray-500">
                  This key will be used to make API requests and generate code snippets.
                </p>
              </div>
            </div>

            <div className="ml-4"> {/* Ensure button is aligned if layout breaks */}
              <button
                onClick={() => setShowCodeGenerator(!showCodeGenerator)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <CodeBracketIcon className="h-4 w-4 mr-2" />
                Code
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <tab.icon className="h-5 w-5 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'builder' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <RequestBuilder
                  request={currentRequest}
                  onChange={setCurrentRequest}
                  onExecute={handleExecuteRequest}
                  isLoading={isLoading}
                />
              </div>
              <div>
                <ResponseViewer response={currentResponse} isLoading={isLoading} />
              </div>
            </div>
          )}

          {activeTab === 'swagger' && (
            <ApiDocumentation apiKey={manualApiKeyInput} />
          )}

          {activeTab === 'templates' && (
            <ApiTemplates onTemplateSelect={handleTemplateSelect} />
          )}

          {activeTab === 'history' && (
            <RequestHistory 
              onRequestSelect={(request) => {
                setCurrentRequest(request);
                setActiveTab('builder');
              }}
            />
          )}
        </div>
      </div>

      {/* Code Generator Modal */}
      {showCodeGenerator && (
        <CodeGenerator
          request={currentRequest}
          apiKey={manualApiKeyInput} // Pass the manual key string
          onClose={() => setShowCodeGenerator(false)}
        />
      )}
    </div>
  );
};

export default ApiPlaygroundPage;
